/**
 * @file settings-form-utils.ts
 *
 * Pure helpers for the Settings form: deriving the editable fields from the
 * loaded shop settings, a dirty comparison, a small email check, and building the
 * `Partial<ShopSettings>` patch sent on save. Keeping these pure makes the mapping
 * unit-testable without rendering. The shopfront language is the shop's `locale`,
 * loaded and saved with the rest of the settings (no separate endpoint).
 */

import type { ShopSettings } from '../api/shop-types';
import type { SettingsFormFields } from './settings-form-types';

/**
 * Derives the editable form fields from the loaded settings. The shopfront language
 * is the shop's `locale`, carried on the settings themselves.
 *
 * @param settings - The loaded shop settings, or `undefined` before they load.
 *
 * @returns The initial {@link SettingsFormFields}.
 */
export function deriveSettingsFields(
  settings: ShopSettings | undefined,
): SettingsFormFields {
  return {
    shopName: settings?.title ?? '',
    streetAddress: settings?.street_address ?? '',
    zipCode: settings?.postcode ?? '',
    city: settings?.city ?? '',
    country: settings?.country ?? '',
    email: settings?.email ?? '',
    emailLanguage: settings?.language ?? '',
    currencyPosition: settings?.currency_position ?? '',
    dateFormat: settings?.date_format ?? '',
    numberFormat: settings?.number_format ?? '',
    currency: settings?.currency ?? '',
    weightUnit: settings?.weight_unit ?? '',
    timezone: settings?.timezone ?? 'UTC',
    shopfrontLanguage: settings?.locale ?? '',
  };
}

/**
 * Shallow-compares two field sets for dirty tracking.
 *
 * @param a - One field set.
 * @param b - The other field set.
 *
 * @returns `true` when every field is strictly equal.
 */
export function shallowEqual(a: SettingsFormFields, b: SettingsFormFields): boolean {
  const keys = Object.keys(a) as (keyof SettingsFormFields)[];
  return keys.every((key) => a[key] === b[key]);
}

/** Loose email check, mirroring WSB-v2's Yup `.email()` intent (non-empty local@domain.tld). */
export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/**
 * Builds the shop-settings write patch from the form's fields. The shopfront language
 * maps to the shop's `locale`, persisted with the rest of the settings.
 *
 * @param fields - The current form fields.
 *
 * @returns The {@link ShopSettings} patch to persist.
 */
export function buildSettingsPatch(fields: SettingsFormFields): Partial<ShopSettings> {
  return {
    title: fields.shopName.trim(),
    email: fields.email.trim(),
    language: fields.emailLanguage,
    date_format: fields.dateFormat,
    street_address: fields.streetAddress.trim(),
    city: fields.city.trim(),
    postcode: fields.zipCode.trim(),
    country: fields.country,
    currency: fields.currency,
    currency_position: fields.currencyPosition,
    number_format: fields.numberFormat,
    weight_unit: fields.weightUnit,
    timezone: fields.timezone,
    locale: fields.shopfrontLanguage,
  };
}
